import React from "react";
import {
  ActivityIndicator,
  FlatList,
  NativeSyntheticEvent,
  Pressable,
  PressableProps,
  StyleProp,
  StyleSheet,
  Text,
  TextInput,
  TextInputKeyPressEventData,
  TextInputProps,
  TextStyle,
  View,
  ViewStyle,
} from "react-native";
import { PdChevronDown, PdChevronUp } from "src/components/Icons";
import { PdText } from "src/components/layout";
import { tPressableState } from "src/components/types";
import { mergeRefs } from "src/components/utils";

import { kColors } from "src/kColors";
import { kFonts } from "src/kFonts";
import { kStyleConst } from "src/kStyle";
import { useOnClickOutside } from "usehooks-ts";
import { PdInput, PdInputGroup, PdInputRightAddon } from "./input";

export type tPdSelectOption = {
  value: string;
  label: string;
};

export type tPdSelectProps = {
  value?: tPdSelectOption | null;
  onChange?: (option: tPdSelectOption | null) => void;
  options: tPdSelectOption[];
  valueExtractor?: (option?: tPdSelectOption | null) => string;
  labelExtractor?: (option?: tPdSelectOption | null) => string;
  direction?: "bottom" | "top";
  isDisabled?: boolean;
  isLoading?: boolean;
  style?: StyleProp<ViewStyle>;
  textInputStyle?: StyleProp<TextStyle>;
} & Omit<TextInputProps, "style" | "value" | "onChange">;

export type tPdSelectMultipleProps = {
  value?: tPdSelectOption[] | null;
  onChange?: (option: tPdSelectOption[]) => void;
  style?: StyleProp<ViewStyle>;
  selectStyle?: StyleProp<TextStyle>;
} & Omit<tPdSelectProps, "style" | "value" | "onChange">;

const EmptyMenuItem = () => (
  <View style={styles.emptyMenuItem}>
    <Text numberOfLines={1} style={styles.emptyMenuItemLabel}>
      <Text>No results</Text>
    </Text>
  </View>
);

const PdMenuItem = (
  props: {
    value?: tPdSelectOption | null;
    item: tPdSelectOption;
    searchTerm: string;
    labelExtractor?: (option: tPdSelectOption) => string;
  } & PressableProps,
) => {
  const { value, item, searchTerm, labelExtractor, ...rest } = props;
  const menuItemRef = React.useRef<View>(null);

  const label = item?.label || labelExtractor?.(item) || "";
  const labelIsSelected = value?.label === label;
  const aHighlightIndex = label.toLowerCase().indexOf(searchTerm.toLowerCase());
  const startText = label.substring(0, aHighlightIndex);
  const highlightedText = label.substr(aHighlightIndex, searchTerm.length);
  const endText = label.substring(aHighlightIndex + searchTerm.length);

  return (
    <Pressable ref={menuItemRef} accessibilityRole="menuitem" {...rest}>
      {({ hovered, focused }: tPressableState) => (
        <View style={[hovered || focused || labelIsSelected ? styles.selectedMenuItem : {}, styles.menuItem]}>
          <PdText variant="body-sm" numberOfLines={1} style={labelIsSelected ? styles.selectedMenuItemLabel : {}}>
            {startText}
            <PdText variant="body-sm" style={styles.menuItemLabelHighlight}>
              {highlightedText}
            </PdText>
            {endText}
          </PdText>
        </View>
      )}
    </Pressable>
  );
};

export const PdSelect = React.forwardRef<TextInput, tPdSelectProps>((props, ref) => {
  const {
    value,
    onChange,
    options,
    valueExtractor,
    labelExtractor,
    direction = "bottom",
    isDisabled = false,
    isLoading = false,
    placeholder,
    textInputStyle,
    style,
    ...textInputProps
  } = props;

  const [searchTerm, setSearchTerm] = React.useState("");
  const [isOpen, setOpen] = React.useState(false);
  const containerRef = React.useRef<View>(null);
  const textInputRef = React.useRef<TextInput>(null);

  const optionsFiltered = React.useMemo(
    () =>
      options.filter((option) => {
        const label = option?.label || labelExtractor?.(option) || "";
        return label.toLowerCase().includes(searchTerm.toLowerCase());
      }),
    [searchTerm, labelExtractor, options],
  );

  const handleOnChangeTextInput = (newValue: string) => {
    setSearchTerm(newValue);
  };

  const handleOnPressTextInput = () => {
    if (isDisabled) return;
    textInputRef.current?.focus();
    setOpen(true);
  };

  const handleOnPressMenuItem = (item: tPdSelectOption) => {
    textInputRef.current?.focus();
    onChange?.(item);
    setSearchTerm("");
    setOpen(false);
  };

  const ChevronIcon = isOpen ? PdChevronUp : PdChevronDown;
  const controlledValue = value?.label || labelExtractor?.(value);

  const handleOnKeyPress = (event: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
    if (isDisabled) return;

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const eventCode = event.code;
    if (eventCode === "Enter" || (eventCode === "ArrowDown" && !isOpen)) {
      setOpen(true);
    } else if (eventCode === "Escape") {
      setOpen(false);
    }
  };

  const handleClickOutside = () => {
    setOpen(false);
  };

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  useOnClickOutside(containerRef, handleClickOutside);

  return (
    <PdInputGroup ref={containerRef} style={[style, isDisabled || isLoading ? styles.pdSelectDisabled : {}]}>
      <Pressable
        disabled={isDisabled || isLoading}
        focusable={false}
        onPress={handleOnPressTextInput}
        style={styles.pdSelectContainerButton}
      >
        <PdInput
          ref={mergeRefs([ref, textInputRef])}
          focusable
          value={searchTerm}
          placeholder={controlledValue || placeholder}
          placeholderTextColor={controlledValue ? kColors.brandDark : undefined}
          onChangeText={handleOnChangeTextInput}
          onKeyPress={handleOnKeyPress}
          style={[styles.pdSelectInput, textInputStyle]}
          editable={!isDisabled && !isLoading}
          {...textInputProps}
        />
        {isLoading && (
          <PdInputRightAddon style={styles.pdSelectIconContainer}>
            <ActivityIndicator
              // size="small"
              color={kColors.brandDarkGreen}
              style={{ transform: [{ scaleX: 0.7 }, { scaleY: 0.7 }] }}
            />
          </PdInputRightAddon>
        )}
        {!isLoading && (
          <PdInputRightAddon style={styles.pdSelectIconContainer} focusable={false}>
            <ChevronIcon focusable={false} style={styles.pdSelectIcon} />
          </PdInputRightAddon>
        )}
      </Pressable>
      {!!(isOpen || searchTerm) && (
        <FlatList
          style={[
            styles.pdSelectMenuList,
            direction === "bottom" ? styles.pdSelectMenuListBottom : {},
            direction === "top" ? styles.pdSelectMenuListTop : {},
          ]}
          data={optionsFiltered}
          renderItem={({ item }) => (
            <PdMenuItem
              value={value}
              item={item}
              searchTerm={searchTerm}
              labelExtractor={labelExtractor}
              onPress={() => {
                handleOnPressMenuItem?.(item);
              }}
            />
          )}
          ListEmptyComponent={EmptyMenuItem}
          keyExtractor={(item) => item?.value || (valueExtractor?.(item) ?? "")}
        />
      )}
    </PdInputGroup>
  );
});

const styles = StyleSheet.create({
  emptyMenuItem: {
    paddingHorizontal: 2 * kStyleConst.kSpacing,
    paddingVertical: kStyleConst.kSpacing,
  },
  emptyMenuItemLabel: {
    ...kFonts.body,
    color: kColors.brandDark,
    lineHeight: 25,
  },
  menuItem: {
    paddingHorizontal: 2 * kStyleConst.kSpacing,
    paddingVertical: kStyleConst.kSpacing,
  },
  menuItemLabelHighlight: {
    fontWeight: "500",
  },
  pdSelectContainerButton: {
    alignItems: "center",
    flexDirection: "row",
    flex: 1,
  },
  pdSelectDisabled: {
    opacity: 0.4,
  },
  pdSelectIcon: {
    height: 8,
    width: 8,
  },
  pdSelectIconContainer: {
    alignItems: "center",
    justifyContent: "center",
    width: 12,
  },
  pdSelectInput: {
    borderWidth: 0,
    flex: 1,
  },
  pdSelectMenuList: {
    backgroundColor: kColors.white,
    borderColor: kColors.gray300,
    borderRadius: kStyleConst.kSpacing,
    borderWidth: 1,
    left: 0,
    maxHeight: 150,
    overflowY: "auto",
    position: "absolute",
    right: 0,
  },
  pdSelectMenuListBottom: {
    top: 45,
  },
  pdSelectMenuListTop: {
    bottom: 45,
  },
  selectedMenuItem: {
    backgroundColor: kColors.gray50,
  },
  selectedMenuItemLabel: {
    fontWeight: "500",
  },
});
